import { useEffect, useRef, useState } from "react";
import type { ActiveJob, Job, JobLog } from "./types";

// Live progress over Server-Sent Events. EventSource reconnects automatically; callers also keep a
// slow polling query as a fallback, so a broken stream degrades to periodic refresh instead of failing.

export function useJobStream(jobId: number | null, enabled = true) {
  const [job, setJob] = useState<Job | null>(null);
  const [logs, setLogs] = useState<JobLog[]>([]);
  const [connected, setConnected] = useState(false);
  const lastLog = useRef(0);

  useEffect(() => {
    setLogs([]);
    setJob(null);
    lastLog.current = 0;
  }, [jobId]);

  useEffect(() => {
    if (!jobId || !enabled || typeof EventSource === "undefined") return;
    const es = new EventSource(`/api/events/stream?job_id=${jobId}&after_log=${lastLog.current}`);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener("job", (e) => {
      try {
        setJob(JSON.parse((e as MessageEvent).data));
      } catch {
        /* ignore malformed event */
      }
    });
    es.addEventListener("log", (e) => {
      try {
        const entry: JobLog = JSON.parse((e as MessageEvent).data);
        if (entry.id <= lastLog.current) return;
        lastLog.current = entry.id;
        setLogs((prev) => (prev.length > 2000 ? [...prev.slice(-1500), entry] : [...prev, entry]));
      } catch {
        /* ignore */
      }
    });
    return () => {
      es.close();
      setConnected(false);
    };
  }, [jobId, enabled]);

  return { job, logs, connected };
}

export function useActiveJobs() {
  const [jobs, setJobs] = useState<ActiveJob[] | null>(null);
  const [connected, setConnected] = useState(false);
  useEffect(() => {
    if (typeof EventSource === "undefined") return;
    const es = new EventSource("/api/events/stream");
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);
    es.addEventListener("jobs", (e) => {
      try {
        setJobs(JSON.parse((e as MessageEvent).data));
      } catch {
        /* ignore */
      }
    });
    return () => es.close();
  }, []);
  return { jobs, connected };
}
